import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle,
  FileDown,
  Info,
  Loader2,
  Printer,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { cn, formatPercent } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type {
  MigrationReport,
  ReportAiAnalysis,
  ReportGroupSnapshot,
  ReportLotSnapshot,
  ReportOverallStatus,
  ReportSnapshot,
} from './types';
import { formatLotSummary } from './components/autoCatchUpPresentation';

const POLL_INTERVAL_MS = 30_000;
/** Anti-boucle : pas de régénération IA automatique plus d'une fois par minute. */
const AUTO_REGEN_COOLDOWN_MS = 60_000;

const OVERALL_META: Record<ReportOverallStatus, { label: string; className: string; Icon: typeof CheckCircle }> = {
  on_track: {
    label: 'Migration conforme',
    className: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950/30 dark:border-green-900 dark:text-green-200',
    Icon: CheckCircle,
  },
  attention: {
    label: "Points d'attention",
    className: 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-950/30 dark:border-orange-900 dark:text-orange-200',
    Icon: AlertTriangle,
  },
  critical: {
    label: 'Anomalies critiques',
    className: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200',
    Icon: ShieldAlert,
  },
};

const GROUP_STATUS_META: Record<ReportGroupSnapshot['status'], { label: string; className: string }> = {
  completed: { label: 'Migré', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  completed_partial: { label: 'Migré avec réserves', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  not_started: { label: 'Non commencé', className: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300' },
};

const LOT_STATUS_LABELS: Record<string, string> = {
  pending: 'À migrer',
  extracting: 'Extraction…',
  extract_failed: 'Échec extraction',
  mapping: 'Mapping…',
  map_failed: 'Échec mapping',
  validating: 'Validation…',
  validation_failed: 'Échec validation',
  migrating: 'Migration…',
  migrate_failed: 'Échec migration',
  completed: 'Migré',
  completed_with_errors: 'Migré avec erreurs',
  skipped: 'Ignoré',
  bypassed: 'Bypassé',
};

const SEVERITY_META: Record<'info' | 'warning' | 'critical', { className: string; Icon: typeof Info }> = {
  info: { className: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200', Icon: Info },
  warning: { className: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200', Icon: AlertTriangle },
  critical: { className: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200', Icon: XCircle },
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (n: number) => n.toLocaleString('fr-FR');

function lotRowTone(lot: ReportLotSnapshot): string {
  if (lot.status.includes('failed')) return 'bg-red-50/60 dark:bg-red-950/20';
  if (lot.failedRecords > 0 || lot.status === 'completed_with_errors') return 'bg-orange-50/60 dark:bg-orange-950/20';
  if (lot.status === 'pending' && lot.totalRecords > 0) return 'bg-yellow-50/50 dark:bg-yellow-950/10';
  return '';
}

/** Tableau des lots d'un groupe — données exactes issues du snapshot SQL. */
function GroupLotsTable({ group }: { group: ReportGroupSnapshot }) {
  return (
    <div className="overflow-x-auto rounded-lg border dark:border-slate-700 print:border-gray-300">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500 print:bg-gray-100">
            <th className="text-left py-2 px-3 font-medium">Lot</th>
            <th className="text-right py-2 px-3 font-medium">À migrer</th>
            <th className="text-right py-2 px-3 font-medium">Migrés</th>
            <th className="text-right py-2 px-3 font-medium">Échecs</th>
            <th className="text-right py-2 px-3 font-medium">Exclus</th>
            <th className="text-right py-2 px-3 font-medium">Avancement</th>
            <th className="text-left py-2 px-3 font-medium">Statut</th>
          </tr>
        </thead>
        <tbody>
          {group.lots.map((lot) => (
            <tr key={lot.code} className={cn('border-b last:border-b-0 dark:border-slate-700', lotRowTone(lot))}>
              <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200">{lot.name}</td>
              <td className="py-2 px-3 text-right tabular-nums">
                {lot.estimated || lot.totalRecords > 0 ? formatNumber(lot.totalRecords) : '?'}
              </td>
              <td className="py-2 px-3 text-right tabular-nums text-green-700 dark:text-green-400">
                {formatNumber(lot.migratedRecords)}
              </td>
              <td className={cn('py-2 px-3 text-right tabular-nums', lot.failedRecords > 0 ? 'text-red-600 font-semibold' : 'text-gray-400')}>
                {formatNumber(lot.failedRecords)}
              </td>
              <td className="py-2 px-3 text-right tabular-nums text-gray-400">
                {formatNumber(lot.excludedRecords ?? 0)}
              </td>
              <td className="py-2 px-3 text-right tabular-nums">
                {lot.totalRecords > 0 ? `${formatPercent(lot.progressPercent)}%` : '—'}
              </td>
              <td className="py-2 px-3">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {LOT_STATUS_LABELS[lot.status] ?? lot.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-50 dark:bg-slate-800/60 font-medium print:bg-gray-100">
            <td className="py-2 px-3">Total du groupe</td>
            <td className="py-2 px-3 text-right tabular-nums">{formatNumber(group.totals.records)}</td>
            <td className="py-2 px-3 text-right tabular-nums text-green-700 dark:text-green-400">
              {formatNumber(group.totals.migrated)}
            </td>
            <td className={cn('py-2 px-3 text-right tabular-nums', group.totals.failed > 0 ? 'text-red-600' : 'text-gray-400')}>
              {formatNumber(group.totals.failed)}
            </td>
            <td className="py-2 px-3 text-right tabular-nums text-gray-400">
              {formatNumber(group.totals.excluded ?? 0)}
            </td>
            <td className="py-2 px-3 text-right tabular-nums">{formatPercent(group.progressPercent)}%</td>
            <td className="py-2 px-3" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Rapport d'audit de migration d'un projet.
 *
 * Les tableaux reflètent TOUJOURS l'état courant (snapshot recalculé à chaque
 * consultation et re-poll périodique) ; l'analyse rédigée (Gemini) provient du
 * dernier rapport généré et se régénère automatiquement quand l'avancement a
 * changé. Export PDF via l'impression du navigateur (mise en page dédiée).
 */
export function ProjectReportPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission('action.migration_v1_v2.execute');

  const [snapshot, setSnapshot] = useState<ReportSnapshot | null>(null);
  const [currentHash, setCurrentHash] = useState<string | null>(null);
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const lastAutoRegenAt = useRef(0);
  const generatingRef = useRef(false);

  const school = snapshot?.project.school;
  useBreadcrumbLabel(school?.name ? `Rapport — ${school.name}` : null);

  const generate = useCallback(async () => {
    if (!canGenerate || generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    try {
      const res = await migrationV1V2Api.generateProjectReport(projectId);
      setReport(res.report);
      setAiEnabled(res.aiEnabled);
    } catch {
      setError("La génération du rapport a échoué. Les tableaux ci-dessous restent à jour.");
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  }, [projectId, canGenerate]);

  const fetchReport = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const res = await migrationV1V2Api.getProjectReport(projectId);
        setSnapshot(res.current.snapshot);
        setCurrentHash(res.current.dataHash);
        setReport(res.report);
        setAiEnabled(res.aiEnabled);
        setError('');

        // Mise à jour au fil de l'avancement : régénère l'analyse rédigée si
        // elle manque ou ne reflète plus l'état courant (avec anti-boucle).
        const stale = !res.report || res.report.dataHash !== res.current.dataHash;
        const cooledDown = Date.now() - lastAutoRegenAt.current > AUTO_REGEN_COOLDOWN_MS;
        if (stale && cooledDown && !generatingRef.current) {
          lastAutoRegenAt.current = Date.now();
          void generate();
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (!silent) {
          setError(
            status === 403
              ? "Vous n'avez pas accès à ce dossier de migration : il est réservé au responsable assigné et aux administrateurs."
              : 'Impossible de charger le rapport'
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [projectId, generate]
  );

  useEffect(() => {
    fetchReport();
    const timer = setInterval(() => fetchReport(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchReport]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error && !snapshot) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild>
          <Link to={`/migration-v1-v2/${projectId}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au projet
          </Link>
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!snapshot) return null;

  const analysis: ReportAiAnalysis | null = report?.aiAnalysis ?? null;
  const overall = analysis ? OVERALL_META[analysis.overallStatus] ?? OVERALL_META.attention : null;
  const analysisStale = !!report && report.dataHash !== currentHash;
  const groupAnalysisByCategory = new Map(
    (analysis?.groupAnalyses ?? []).map((g) => [g.category, g])
  );
  const anomalies = snapshot.anomalies;
  const hasAnomalies =
    anomalies.lotsPartial.length > 0 ||
    anomalies.lotsNotStarted.length > 0 ||
    anomalies.lotsFailed.length > 0;
  const exclusions = snapshot.exclusions;
  const hasExclusions = (exclusions?.total ?? 0) > 0;
  const autoCatchUp = snapshot.autoCatchUp;
  const hasAutoCatchUp = (autoCatchUp?.runs?.length ?? 0) > 0;

  return (
    <div className="space-y-6 print:space-y-4 print:text-[13px]">
      {/* Barre d'actions (masquée à l'impression) */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Button variant="ghost" asChild>
          <Link to={`/migration-v1-v2/${projectId}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au projet
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          {canGenerate && (
            <Button variant="outline" size="sm" onClick={() => generate()} disabled={generating}>
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Régénérer l'analyse
            </Button>
          )}
          <Button size="sm" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5" />
            Exporter en PDF
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 text-sm text-orange-800 print:hidden">
          {error}
        </div>
      )}

      {/* En-tête du rapport */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6 print:border-0 print:shadow-none print:p-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Rapport d'audit de migration V1 → V2
            </p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1 print:text-black">
              {school?.name}
              {school?.code && (
                <span className="ml-2 text-base font-mono font-normal text-gray-400">({school.code})</span>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Année V1 {snapshot.project.sourceSchoolYearV1 ?? '—'} → Année V2{' '}
              {snapshot.project.targetSchoolYearV2 ?? '—'}
              {snapshot.project.startedAt && <> · migration démarrée le {formatDateTime(snapshot.project.startedAt)}</>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 print:text-black">
              {formatPercent(snapshot.project.progressPercent)}%
            </p>
            <p className="text-xs text-gray-500">
              {formatNumber(snapshot.project.migratedRecords)} / {formatNumber(snapshot.project.totalRecords)}{' '}
              enregistrements migrés
              {(snapshot.project.excludedRecords ?? 0) > 0 && (
                <> · {formatNumber(snapshot.project.excludedRecords)} exclus</>
              )}
            </p>
          </div>
        </div>
        <Progress value={snapshot.project.progressPercent} className="h-2.5 mt-4" />
        <p className="text-xs text-gray-400 mt-3">
          Données arrêtées au {formatDateTime(snapshot.generatedAt)}
          {report?.generatedAt && <> · analyse rédigée le {formatDateTime(report.generatedAt)}</>}
          {report?.aiGenerated && report.model && <> · IA {report.model}</>}
        </p>
      </div>

      {/* Bandeau d'état global + synthèse */}
      {analysis && overall && (
        <div className={cn('rounded-xl border p-5 print:break-inside-avoid', overall.className)}>
          <div className="flex items-center gap-2 font-semibold">
            <overall.Icon className="h-5 w-5" />
            {overall.label}
            {analysisStale && (
              <span className="inline-flex items-center gap-1 text-xs font-medium opacity-80 print:hidden">
                <Loader2 className={cn('h-3 w-3', generating && 'animate-spin')} />
                {generating ? 'analyse en cours de mise à jour…' : 'analyse plus ancienne que les données'}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm leading-relaxed whitespace-pre-line">{analysis.executiveSummary}</p>
        </div>
      )}
      {!analysis && (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-5 text-sm text-gray-500 flex items-center gap-2">
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Génération de l'analyse en cours…
            </>
          ) : (
            <>Aucune analyse générée pour le moment.</>
          )}
        </div>
      )}

      {/* Ce qui n'est pas (ou partiellement) migré — mise en avant */}
      {(hasAnomalies || (analysis?.unmigratedHighlights.length ?? 0) > 0) && (
        <section className="space-y-3 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 print:text-black">
            Données non migrées ou partiellement migrées
          </h2>

          {analysis?.unmigratedHighlights.map((h, i) => {
            const meta = SEVERITY_META[h.severity] ?? SEVERITY_META.warning;
            return (
              <div key={i} className={cn('rounded-lg border p-3 text-sm flex gap-2.5', meta.className)}>
                <meta.Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">{h.scope} : </span>
                  {h.detail}
                </div>
              </div>
            );
          })}

          {/* Détail chiffré exact (snapshot) */}
          {hasAnomalies && (
            <div className="overflow-x-auto rounded-lg border dark:border-slate-700 print:border-gray-300">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500 print:bg-gray-100">
                    <th className="text-left py-2 px-3 font-medium">Groupe</th>
                    <th className="text-left py-2 px-3 font-medium">Lot</th>
                    <th className="text-left py-2 px-3 font-medium">Situation</th>
                    <th className="text-right py-2 px-3 font-medium">Volumes</th>
                  </tr>
                </thead>
                <tbody>
                  {anomalies.lotsFailed.map((lot) => (
                    <tr key={`failed-${lot.code}`} className="border-b last:border-b-0 dark:border-slate-700 bg-red-50/60 dark:bg-red-950/20">
                      <td className="py-2 px-3 text-gray-500">{lot.group}</td>
                      <td className="py-2 px-3 font-medium">{lot.name}</td>
                      <td className="py-2 px-3 text-red-700 dark:text-red-300">Lot en échec</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(lot.failedRecords ?? 0)} échec{(lot.failedRecords ?? 0) > 1 ? 's' : ''}
                      </td>
                    </tr>
                  ))}
                  {anomalies.lotsPartial.map((lot) => (
                    <tr key={`partial-${lot.code}`} className="border-b last:border-b-0 dark:border-slate-700 bg-orange-50/60 dark:bg-orange-950/20">
                      <td className="py-2 px-3 text-gray-500">{lot.group}</td>
                      <td className="py-2 px-3 font-medium">{lot.name}</td>
                      <td className="py-2 px-3 text-orange-700 dark:text-orange-300">
                        {lot.inProgress ? 'Migration en cours' : 'Partiellement migré'}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(lot.migratedRecords ?? 0)}/{formatNumber(lot.totalRecords ?? 0)} migrés
                        {(lot.failedRecords ?? 0) > 0 && <> · {formatNumber(lot.failedRecords ?? 0)} échecs</>}
                      </td>
                    </tr>
                  ))}
                  {anomalies.lotsNotStarted.map((lot) => (
                    <tr key={`notstarted-${lot.code}`} className="border-b last:border-b-0 dark:border-slate-700 bg-yellow-50/50 dark:bg-yellow-950/10">
                      <td className="py-2 px-3 text-gray-500">{lot.group}</td>
                      <td className="py-2 px-3 font-medium">{lot.name}</td>
                      <td className="py-2 px-3 text-yellow-700 dark:text-yellow-300">Non lancé</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(lot.totalRecords ?? 0)} enregistrement{(lot.totalRecords ?? 0) > 1 ? 's' : ''} V1 en attente
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {anomalies.openManualActions > 0 && (
            <p className="text-sm text-gray-500">
              {anomalies.openManualActions} action{anomalies.openManualActions > 1 ? 's' : ''} manuelle
              {anomalies.openManualActions > 1 ? 's' : ''} en attente de traitement.
            </p>
          )}
        </section>
      )}

      {/* Utilisateurs ajoutés automatiquement après la migration (rattrapage
          des inscriptions V1 restées en dehors de la bascule) */}
      {hasAutoCatchUp && autoCatchUp && (
        <section className="space-y-3 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 print:text-black flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-sky-600" />
            Ajouts automatiques d'utilisateurs
          </h2>

          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {analysis?.autoCatchUpSummary?.trim()
              ? analysis.autoCatchUpSummary
              : `${formatNumber(autoCatchUp.total)} utilisateur${autoCatchUp.total > 1 ? 's ont' : ' a'} été ajouté${autoCatchUp.total > 1 ? 's' : ''} automatiquement après la fin de la migration : il s'agit des personnes inscrites dans l'ancienne plateforme depuis la bascule, reprises chaque nuit et rattachées à leur classe et à leurs groupes. Les fiches déjà migrées ne sont pas modifiées ; seules les affectations de classe suivent l'ancienne plateforme : un élève changé de classe là-bas est déplacé ici.`}
          </p>

          {autoCatchUp.runs.map((run) => {
            const lots = (run.lots ?? []).filter(
              (l) =>
                l.added > 0 ||
                (l.reassigned ?? 0) > 0 ||
                (l.preserved ?? 0) > 0 ||
                (l.attached ?? 0) > 0 ||
                l.failed > 0 ||
                l.error
            );
            const relinked = run.relinkedRecords ?? 0;
            return (
              <div
                key={run.id}
                className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3 print:border-gray-300 print:break-inside-avoid"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatDateTime(run.startedAt)}
                    </span>
                    <span className="text-gray-500">
                      {' '}
                      · {run.trigger === 'cron' ? 'tâche programmée' : 'lancé manuellement'}
                      {run.triggeredBy && ` par ${run.triggeredBy.firstname} ${run.triggeredBy.lastname}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm tabular-nums">
                    <span className="rounded-md bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      {formatNumber(run.addedRecords)} ajouté{run.addedRecords > 1 ? 's' : ''}
                    </span>
                    {relinked > 0 && (
                      <span className="rounded-md bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {formatNumber(relinked)} rattachement{relinked > 1 ? 's' : ''} modifié{relinked > 1 ? 's' : ''}
                      </span>
                    )}
                    {run.failedRecords > 0 && (
                      <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        {formatNumber(run.failedRecords)} en échec
                      </span>
                    )}
                  </div>
                </div>

                {run.error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                    {run.error}
                  </div>
                )}

                {run.additions.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border dark:border-slate-700 print:border-gray-300">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500 print:bg-gray-100">
                          <th className="text-left py-2 px-3 font-medium">Personne concernée</th>
                          <th className="text-left py-2 px-3 font-medium">Type</th>
                          <th className="text-left py-2 px-3 font-medium">Résultat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {run.additions.map((addition, i) => (
                          <tr key={i} className="border-b last:border-b-0 dark:border-slate-700">
                            <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200">
                              {addition.label}
                            </td>
                            <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{addition.lotName}</td>
                            <td className="py-2 px-3 text-gray-600 dark:text-gray-300">
                              {addition.status !== 'migrated' ? (
                                <span className="text-red-700 dark:text-red-300">
                                  Échec{addition.error ? ` — ${addition.error}` : ''}
                                </span>
                              ) : addition.kind === 'reassigned' ? (
                                <span className="text-violet-700 dark:text-violet-300">
                                  Changé de classe{addition.change ? ` — ${addition.change}` : ''}
                                </span>
                              ) : addition.kind === 'preserved' ? (
                                <span className="text-orange-700 dark:text-orange-300">
                                  Affectation V2 conservée, à arbitrer{addition.change ? ` — ${addition.change}` : ''}
                                </span>
                              ) : addition.kind === 'attached' ? (
                                <span className="text-green-700 dark:text-green-300">Rattaché</span>
                              ) : (
                                <span className="text-green-700 dark:text-green-300">Ajouté</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {run.additionsTruncated && (
                  <p className="text-xs text-gray-400">
                    Liste limitée aux {formatNumber(run.additions.length)} premières personnes — les
                    compteurs ci-dessus restent exacts.
                  </p>
                )}

                {lots.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Lots rejoués : {lots.map(formatLotSummary).join(', ')}
                  </p>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* Éléments volontairement exclus de la migration (avec raisons) */}
      {hasExclusions && exclusions && (
        <section className="space-y-3 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 print:text-black flex items-center gap-2">
            <Ban className="h-4 w-4 text-gray-500" />
            Éléments exclus de la migration
          </h2>

          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {analysis?.exclusionsSummary?.trim()
              ? analysis.exclusionsSummary
              : `${formatNumber(exclusions.total)} élément${exclusions.total > 1 ? 's ont' : ' a'} été volontairement écarté${exclusions.total > 1 ? 's' : ''} de la migration — donnée d'origine inutilisable ou décision de l'équipe. Ces éléments ne comptent pas dans les pourcentages de migration ; la raison de chaque exclusion figure ci-dessous.`}
          </p>

          <div className="overflow-x-auto rounded-lg border dark:border-slate-700 print:border-gray-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500 print:bg-gray-100">
                  <th className="text-left py-2 px-3 font-medium">Domaine</th>
                  <th className="text-left py-2 px-3 font-medium">Lot</th>
                  <th className="text-left py-2 px-3 font-medium">Élément</th>
                  <th className="text-left py-2 px-3 font-medium">Raison de l'exclusion</th>
                  <th className="text-left py-2 px-3 font-medium">Origine</th>
                </tr>
              </thead>
              <tbody>
                {exclusions.items.map((item, i) => (
                  <tr key={i} className="border-b last:border-b-0 dark:border-slate-700">
                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{item.group}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{item.lotName}</td>
                    <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200">
                      {item.element}
                    </td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{item.reason}</td>
                    <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">
                      {item.manual ? 'Décision de l\'équipe' : 'Automatique'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {exclusions.truncated && (
            <p className="text-xs text-gray-400">
              Liste limitée aux {formatNumber(exclusions.items.length)} premiers éléments sur{' '}
              {formatNumber(exclusions.total)} exclus — le détail complet est consultable dans
              chaque lot du projet.
            </p>
          )}
        </section>
      )}

      {/* Détail par groupe de lots */}
      <section className="space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 print:text-black">
          Avancement par domaine
        </h2>
        {snapshot.groups.map((group) => {
          const groupAnalysis = groupAnalysisByCategory.get(group.category);
          const statusMeta = GROUP_STATUS_META[group.status];
          // « Sans objet » uniquement si on SAIT que le groupe est vide (tous
          // les lots estimés à 0). Un groupe jamais estimé reste « Non
          // commencé » avec sa volumétrie inconnue.
          const allEstimated = group.lots.every((l) => l.estimated || l.totalRecords > 0);
          const isEmpty = group.totals.records === 0 && group.status === 'not_started' && allEstimated;
          return (
            <div
              key={group.category}
              className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-5 space-y-3 print:border-gray-300 print:shadow-none print:break-inside-avoid"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 print:text-black">
                  {group.label}
                </h3>
                <div className="flex items-center gap-2">
                  {group.totals.records > 0 && (
                    <span className="text-sm text-gray-500 tabular-nums">
                      {formatNumber(group.totals.migrated)}/{formatNumber(group.totals.records)} ·{' '}
                      {formatPercent(group.progressPercent)}%
                    </span>
                  )}
                  <span className={cn('rounded-md px-2 py-0.5 text-xs font-medium', statusMeta.className)}>
                    {isEmpty ? 'Sans objet' : statusMeta.label}
                  </span>
                </div>
              </div>

              {groupAnalysis && (
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {groupAnalysis.analysis}
                </p>
              )}
              {(groupAnalysis?.alerts ?? []).map((alert, i) => (
                <div
                  key={i}
                  className="rounded-md border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30 px-3 py-2 text-xs text-orange-800 dark:text-orange-200 flex gap-2"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                  {alert}
                </div>
              ))}

              {!isEmpty && <GroupLotsTable group={group} />}
              {!isEmpty && !allEstimated && (
                <p className="text-xs text-gray-400">
                  « ? » : volumétrie non encore estimée (lancer « Charger les données V1 » sur le projet).
                </p>
              )}
              {isEmpty && (
                <p className="text-sm text-gray-400 italic">
                  Aucune donnée de ce type pour cette école — domaine sans objet.
                </p>
              )}
            </div>
          );
        })}
      </section>

      {/* Risques & recommandations */}
      {analysis && ((analysis.risks?.length ?? 0) > 0 || analysis.recommendations.length > 0) && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 print:break-inside-avoid">
          {(analysis.risks?.length ?? 0) > 0 && (
            <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-5 print:border-gray-300 print:shadow-none">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 print:text-black">
                Risques identifiés
              </h3>
              <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc pl-5">
                {analysis.risks!.map((risk, i) => (
                  <li key={i}>{risk}</li>
                ))}
              </ul>
            </div>
          )}
          {analysis.recommendations.length > 0 && (
            <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-5 print:border-gray-300 print:shadow-none">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 print:text-black">
                Recommandations
              </h3>
              <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc pl-5">
                {analysis.recommendations.map((reco, i) => (
                  <li key={i}>{reco}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Conclusion */}
      {analysis?.conclusion && (
        <section className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-5 print:border-gray-300 print:shadow-none print:break-inside-avoid">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 print:text-black">
            Conclusion
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
            {analysis.conclusion}
          </p>
        </section>
      )}

      {/* Pied de rapport */}
      <footer className="text-xs text-gray-400 border-t dark:border-slate-700 pt-3 flex items-center justify-between flex-wrap gap-2 print:border-gray-300">
        <span className="inline-flex items-center gap-1.5">
          {report?.aiGenerated ? (
            <>
              <Sparkles className="h-3 w-3" />
              Analyse rédigée par IA ({report.model}) à partir des compteurs exacts de migration — tableaux
              issus directement de la base, sans intervention de l'IA.
            </>
          ) : aiEnabled ? (
            'Analyse rédigée indisponible pour cette génération — tableaux exacts.'
          ) : (
            "IA désactivée (clé Gemini absente) — rapport chiffré uniquement."
          )}
        </span>
        <span className="inline-flex items-center gap-1.5 print:hidden">
          <FileDown className="h-3 w-3" />
          L'export PDF utilise l'impression du navigateur (choisir « Enregistrer au format PDF »).
        </span>
      </footer>
    </div>
  );
}
